import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { AppOverviewDto, CheckStatus, CheckSummaryDto } from '@ops-monitor/shared';

import { monitoring } from '../api/client';
import { StatusDot } from '../components/StatusDot';
import { CheckDrawer } from '../components/CheckDrawer';
import { ErrorState, Spinner } from '../components/ui';

const BADGE_ORDER: CheckStatus[] = ['ok', 'warn', 'crit', 'nodata'];
const BADGE_LABEL: Record<CheckStatus, string> = { ok: 'норма', warn: 'внимание', crit: 'проблема', nodata: 'нет данных' };

/**
 * Landing page: the cloud BALANCES strip pinned to the very top (owner:
 * balances are the first thing to see), then one card per platform —
 * worst-of status, clickable badge counts (each jumps to the app page
 * pre-filtered by that status), and the headline KPI tiles.
 */
export function OverviewPage() {
  const navigate = useNavigate();
  const apps = useQuery({ queryKey: ['apps'], queryFn: ({ signal }) => monitoring.apps(signal), refetchInterval: 30_000 });
  const [open, setOpen] = useState<CheckSummaryDto | null>(null);

  if (apps.isPending) return <Spinner label="Загружаю состояние" />;
  if (apps.isError) return <ErrorState message="Не удалось загрузить состояние" />;
  const balances = apps.data.items.find((item) => item.app.key === 'billing');

  return (
    <>
      <h1 className="page__title">Состояние платформ</h1>
      <p className="page__subtitle">
        Главные параметры обеих платформ в реальном времени. Нажмите на плитку — детали, история, оповещения и запуск вручную.
        Клик по счётчику статуса — сразу к проблемам этого типа.
      </p>
      {balances ? <BalanceStrip item={balances} onOpen={setOpen} /> : null}
      <div className="apps-grid">
        {apps.data.items
          .filter((item) => item.app.key !== 'billing') // rendered as the top strip above
          .map((item) => (
            <AppCard key={item.app.key} item={item} onOpen={setOpen} onOpenApp={(status) => navigate(`/app/${item.app.key}${status ? `?status=${status}` : ''}`)} />
          ))}
      </div>
      {open ? <CheckDrawer check={open} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

/** Full-width strip of the seven cloud-balance tiles at the top of Обзор
 *  (the «Балансы» card is intentionally NOT repeated in the grid below —
 *  the nav entry keeps the dedicated page for history and drawers). */
function BalanceStrip({ item, onOpen }: { item: AppOverviewDto; onOpen: (check: CheckSummaryDto) => void }) {
  return (
    <section className="app-card balance-strip">
      <div className="app-card__head">
        <h2 className="app-card__title">{item.app.title}</h2>
        <StatusDot status={item.status} />
      </div>
      <div className="app-card__host">{item.app.host} · обновляется ежечасно · плитка = детали, история и оповещения</div>
      <div className="app-card__tiles">{item.headline.map((check) => <KpiTile key={check.id} check={check} onOpen={onOpen} />)}</div>
    </section>
  );
}

function AppCard({
  item,
  onOpen,
  onOpenApp,
}: {
  item: AppOverviewDto;
  onOpen: (check: CheckSummaryDto) => void;
  onOpenApp: (status: CheckStatus | null) => void;
}) {
  return (
    <section className="app-card">
      <div className="app-card__head">
        <h2 className="app-card__title">{item.app.title}</h2>
        <StatusDot status={item.status} />
      </div>
      <div className="app-card__host">
        {item.app.host} · ns {item.app.namespace} ·{' '}
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => onOpenApp(null)}>
          все проверки →
        </button>
      </div>
      {/* Badge counts are filters: click «проблема 2» to see exactly those two. */}
      <div className="app-card__counts">
        {BADGE_ORDER.map((status) => (
          <button
            key={status}
            type="button"
            className="count-btn"
            onClick={() => onOpenApp(status)}
            title={`Показать проверки в статусе «${BADGE_LABEL[status]}»`}
          >
            <StatusDot status={status} /> {item.counts[status]}
          </button>
        ))}
      </div>
      <div className="app-card__tiles">
        {item.headline.map((check) => (
          <KpiTile key={check.id} check={check} onOpen={onOpen} />
        ))}
      </div>
    </section>
  );
}

function KpiTile({ check, onOpen }: { check: CheckSummaryDto; onOpen: (check: CheckSummaryDto) => void }) {
  return (
    <button type="button" className={`kpi kpi--${check.status}`} onClick={() => onOpen(check)} title={check.why}>
      <div className="kpi__label">{check.title}</div>
      <div className="kpi__value">{check.text}</div>
      <div className="kpi__meta">{check.ranAt ? new Date(check.ranAt).toLocaleTimeString('ru-RU') : '—'}</div>
    </button>
  );
}
